var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/x-buildify-visualization.ts
import { logger } from "@adornis/base/logging.js";
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element.js";
import { ComponentSizeController } from "@adornis/chemistry/controllers/component-size-controller.js";
import { RXController } from "@adornis/chemistry/controllers/RXController.js";
import "@adornis/chemistry/elements/components/x-icon.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import { routingState } from "@adornis/router/client/routing-state.js";
import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { debounceTime, distinctUntilChanged, map } from "rxjs";
import {
  ContentConsumer,
  ExtensionsConsumer,
  ModeConsumer,
  SizeBreakpointConsumer,
  SizeConsumer
} from "./globals/consumer.js";
import { ThrowEventSizeChanged } from "./globals/events.js";
import { getExtensionForData } from "./globals/methods.js";
import "./x-buildify-dropzone.js";
var XBuildifyVisualization = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.jumpToContentID = new RXController(this, routingState.searchParams.get("jump-to-content-id"));
    this._consumedExtensions = ExtensionsConsumer(this);
    this._consumedContent = ContentConsumer(this);
    this._componentSizeController = new ComponentSizeController(this, { triggerUpdates: false });
    this._consumedMode = ModeConsumer(this);
    this._consumedSize = SizeConsumer(this);
    this._consumedBreakpoints = SizeBreakpointConsumer(this);
  }
  connectedCallback() {
    super.connectedCallback();
    this._componentSizeController.observable.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      map((sizeController) => {
        return sizeController.width;
      })
    ).subscribe((width) => {
      ThrowEventSizeChanged(this, width);
    });
  }
  willUpdate(_changedProperties) {
    super.willUpdate(_changedProperties);
    this._contentFilteredSorted = this._consumedContent.value?.filter((base) => base && !this._consumedContent.value?.find((available) => available._id === base.parentID)).sort((a, b) => a.index - b.index);
  }
  async updated(changedProperties) {
    super.updated(changedProperties);
    if (!this._consumedContent.value)
      return;
    let currentElement = this._consumedContent.value.filter((c) => c._id === this.jumpToContentID.value)[0];
    if (!currentElement)
      return;
    const pathToHighlight = [currentElement._id];
    while (currentElement.parentID) {
      pathToHighlight.push(currentElement.parentID);
      const nextEl = this._consumedContent.value.filter((c) => c._id === currentElement?.parentID)[0];
      if (!nextEl)
        return console.error("page is broken, cant scroll to content, parent is missing");
      currentElement = nextEl;
    }
    let idToFind = pathToHighlight.pop();
    let target = this.shadowRoot;
    while (idToFind) {
      console.log("seraching for ", idToFind);
      const elementsToSearch = Array.from(target.querySelectorAll("*:not(x-buildify-dropzone") ?? []);
      const child = elementsToSearch.find(
        (child2) => child2.content?.value?._id === idToFind
      );
      if (!child)
        return logger.warn({ target, child, elementsToSearch, idToFind }, "couldnt find child");
      console.log("found child", child);
      idToFind = pathToHighlight.pop();
      child.scrollIntoView({ behavior: "instant", inline: "center", block: "center" });
      const newTarget = await child.activate(idToFind);
      console.log("acitvating", newTarget);
      if (!newTarget)
        return logger.warn(
          "couldnt open jump-to-content-id until the end because an extension doesn't properly support it"
        );
      target = newTarget;
    }
    const element = target instanceof ShadowRoot ? target.host : target;
    const originalShadow = element.style.boxShadow;
    const originalTransition = element.style.transition;
    element.style.boxShadow = `0 0 20px ${this.colors.accent}`;
    setTimeout(() => {
      element.style.boxShadow = originalShadow;
      element.style.transition = "all 2s ease";
      setTimeout(() => element.style.transition = originalTransition, 2e3);
    }, 3e3);
  }
  render() {
    const extensions = this._consumedExtensions.value;
    if (!extensions)
      return nothing;
    if (this._consumedMode.value === "view")
      return this.__content(extensions);
    return xComponents(html`
      <x-flex space="md">
        ${this.__content(extensions)}
        <x-buildify-dropzone></x-buildify-dropzone>
      </x-flex>
    `);
  }
  get notSelectedColor() {
    return "secondary";
  }
  __content(extensions) {
    if (!this._consumedExtensions.value)
      return nothing;
    const documentExtension = this.documentExtension;
    if (!documentExtension)
      return html` document-extension not found `;
    return documentExtension.render(this.__items(extensions));
  }
  __items(extensions) {
    return html`
      ${repeat(
      this._contentFilteredSorted ?? [],
      (content) => content._id,
      (content, index) => {
        const isLastItem = index === (this._contentFilteredSorted?.length ?? 0) - 1;
        return this.item({ entity: content, extensions, dropzoneBefore: true, dropzoneAfter: isLastItem });
      }
    )}
    `;
  }
  item({
    entity,
    extensions,
    dropzoneBefore,
    dropzoneAfter
  }) {
    const extension = getExtensionForData(entity, extensions);
    if (!extension)
      return nothing;
    return extension.render(entity, { defaultValues: {}, isSubelement: false, dropzoneBefore, dropzoneAfter });
  }
  styles() {
    return [
      ...super.styles(),
      {
        ":host": {
          display: "block",
          width: "100%",
          margin: "0 auto",
          boxSizing: "border-box"
        }
      }
    ];
  }
};
__decorateClass([
  property({ attribute: false })
], XBuildifyVisualization.prototype, "documentExtension", 2);
__decorateClass([
  state()
], XBuildifyVisualization.prototype, "jumpToContentID", 2);
XBuildifyVisualization = __decorateClass([
  customElement("x-buildify-visualization")
], XBuildifyVisualization);
export {
  XBuildifyVisualization
};
